'use client';

/**
 * API namespace `orders` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L6458-L6792).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const orders = {
  /**
   * GET /api/seller/orders?page=&limit=&status= — list the seller's
   * received orders (paginated). Returns `OrderListResponse` (`orders`
   * + total + page + limit). The backend clamps `page >= 0` and `limit`
   * to `[1, 100]` (defense in depth). The optional `status` filter
   * accepts any `OrderStatus` value.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` +
   * `requireMarketOrdersEnabled` + self only (backend verifies
   * `order.sellerId === resolveSellerId(req.user!.sub)` — the seller
   * only sees their own received orders).
   *
   * Spec ref: SPEC-081 Fase 4 §5.2, §4.1 (listSellerOrders).
   */
  async list(
    page = 0,
    limit = 20,
    signal?: AbortSignal,
    sellerId?: string,
  ): Promise<OrderListResponse> {
    // Subtiendas (PR #1511 follow-up + Founder directive 2026-10-31 BE
    // honors the param): when `sellerId` is provided (the active store
    // selected via StoreSwitcher), append it as a query param so the BE
    // filters the seller's received orders by sub-store. The BE now
    // verifies ownership + uses it (resolveSellerId in
    // seller-order.controller.ts).
    const qs = `page=${page}&limit=${limit}${
      sellerId ? `&sellerId=${encodeURIComponent(sellerId)}` : ''
    }`;
    return apiFetch<OrderListResponse>(
      `/seller/orders?${qs}`,
      { signal },
    );
  },
  /**
   * GET /api/seller/orders/:orderNumber — seller's view of a single
   * order. Returns `OrderResponse` (same shape as the buyer's view —
   * Fase 4 doesn't redact fields; Fase 6 may redact buyer's phone
   * after delivery).
   *
   * Authz: self only (ownership pre-check in the controller — 403
   * ORDER_NOT_YOURS on mismatch).
   *
   * Spec ref: SPEC-081 Fase 4 §5.2, §4.1 (getOrderByOrderNumber).
   */
  async get(
    orderNumber: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<OrderResponse> {
    const url = sellerId
      ? `/seller/orders/${encodeURIComponent(orderNumber)}?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/orders/${encodeURIComponent(orderNumber)}`;
    return apiFetch<OrderResponse>(url, { signal });
  },
  /**
   * POST /api/seller/orders/:orderNumber/confirm — seller confirms
   * receipt of a new order (CAS status 'pending_seller' → 'confirmed').
   *
   * Sets `sellerConfirmedAt = now`. After confirmation, the next step
   * depends on the fulfillment method:
   *   - 'ship'           → next: `shipOrder`
   *   - 'pickup'         → next: `markReadyForPickup` (then `deliver`)
   *
   * If the seller does NOT confirm within 72h CALENDAR
   * (INV-MKT-11 + Founder enmienda 2026-08-28), the cron
   * `sellerTimeoutJob` auto-cancels the order + refunds the buyer's
   * credits. So this endpoint is the seller's opportunity to acknowledge
   * the order before the timeout window elapses.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` +
   * `requireMarketOrdersEnabled` + `requireRecent2FA` (5min 2FA —
   * INV-SEC-06 §9.6) + self only.
   *
   * Spec ref: SPEC-081 Fase 4 §5.2, §4.1 (confirmOrder);
   *           SPEC-NEGOCIO §10.5 INV-MKT-11.
   */
  async confirm(
    orderNumber: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<OrderResponse> {
    const url = sellerId
      ? `/seller/orders/${encodeURIComponent(orderNumber)}/confirm?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/orders/${encodeURIComponent(orderNumber)}/confirm`;
    return apiFetch<OrderResponse>(url, { method: 'POST', signal });
  },
  /**
   * POST /api/seller/orders/:orderNumber/ship — seller ships the order
   * with a tracking number + optional tracking photo URL. Only valid
   * when `fulfillmentMethod='ship'` + `status='confirmed'` (CAS
   * enforced by the backend `escrow.service.shipOrder` — Task B).
   *
   * Sets `trackingNumber` + `trackingPhoto` + `shippedAt`. The buyer
   * is notified via Socket.IO `order:shipped` + email.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` +
   * `requireMarketOrdersEnabled` + `requireRecent2FA` (5min 2FA) +
   * self only. Request body validated against `shipOrderSchema`
   * (Task A — Zod).
   *
   * Spec ref: SPEC-081 Fase 4 §5.2, §4.1 (shipOrder); SPEC-NEGOCIO
   *           §10.6 INV-MKT-21.
   */
  async ship(
    orderNumber: string,
    data: ShipOrderRequest,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<OrderResponse> {
    const url = sellerId
      ? `/seller/orders/${encodeURIComponent(orderNumber)}/ship?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/orders/${encodeURIComponent(orderNumber)}/ship`;
    return apiFetch<OrderResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    });
  },
  /**
   * POST /api/seller/orders/:orderNumber/ready — seller marks a pickup
   * order as ready for the buyer to collect. Only valid when
   * `fulfillmentMethod='pickup'` + `status='confirmed'` (CAS enforced
   * by `escrow.service.markReadyForPickup` — Task B).
   *
   * Sets `pickupDetails.sellerConfirmedReadyAt = now`. The buyer is
   * notified via Socket.IO `order:ready_for_pickup` + email.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` +
   * `requireMarketOrdersEnabled` + `requireRecent2FA` (5min 2FA) +
   * self only.
   *
   * Spec ref: SPEC-081 Fase 4 §5.2, §4.1 (markReadyForPickup);
   *           SPEC-NEGOCIO §10.6 INV-MKT-21.
   */
  async ready(
    orderNumber: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<OrderResponse> {
    const url = sellerId
      ? `/seller/orders/${encodeURIComponent(orderNumber)}/ready?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/orders/${encodeURIComponent(orderNumber)}/ready`;
    return apiFetch<OrderResponse>(url, { method: 'POST', signal });
  },
  /**
   * POST /api/seller/orders/:orderNumber/deliver — seller marks the
   * order as delivered. Valid from BOTH 'shipped' AND 'ready_for_pickup'
   * states (CAS enforced by `escrow.service.deliverOrder` — Task B).
   *
   * Sets `deliveredAt = now` + `autoCloseAt = now + 24h` (INV-MKT-10).
   * After delivery, the buyer has 24h to confirm receipt; if they
   * don't, the cron `autoCloseJob` auto-releases the escrow (same
   * 30/2/68 payout as `confirmReceipt`).
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` +
   * `requireMarketOrdersEnabled` + `requireRecent2FA` (5min 2FA) +
   * self only.
   *
   * Spec ref: SPEC-081 Fase 4 §5.2, §4.1 (deliverOrder); SPEC-NEGOCIO
   *           §10.5 INV-MKT-10 + §10.4 INV-MKT-06/07/13.
   */
  async deliver(
    orderNumber: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<OrderResponse> {
    const url = sellerId
      ? `/seller/orders/${encodeURIComponent(orderNumber)}/deliver?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/orders/${encodeURIComponent(orderNumber)}/deliver`;
    return apiFetch<OrderResponse>(url, { method: 'POST', signal });
  },
  // ──────────────────────────────────────────────────────────────────
  // SPEC-081 Fase 5 (Servicios + Digitales — Task C1) — 3 new seller
  // endpoints on `api.seller.orders`:
  //   - `confirmReservation` (service: pending_seller → confirmed —
  //     non-financial calendar commitment).
  //   - `rejectReservation`  (service: pending_seller → cancelled +
  //     INV-MKT-15 full refund of the 50% deposit — financial-grade,
  //     2FA required).
  //   - `completeService`    (service: confirmed → delivered +
  //     autoCloseAt = now + 24h — non-financial state transition).
  //
  // Backend wiring (Task B2 — already merged):
  //   - POST /api/seller/orders/:orderNumber/confirm-reservation
  //     → `sellerOrderController.confirmReservation` →
  //       `escrowService.confirmServiceReservation(orderNumber, sellerId)`.
  //     NO 2FA gate (no financial movement — calendar commitment only).
  //     NO rate limiter (idempotent non-financial operation).
  //   - POST /api/seller/orders/:orderNumber/reject-reservation
  //     → `sellerRejectReservationLimiter` (5/min/user) →
  //       `requireRecent2FA` (5min 2FA — INV-MKT-15 refund) →
  //       `sellerOrderController.rejectReservation` →
  //       `escrowService.rejectServiceReservation(orderNumber,
  //       sellerId, reason)`.
  //   - POST /api/seller/orders/:orderNumber/complete-service
  //     → `sellerOrderController.completeService` →
  //       `escrowService.deliverOrder(orderNumber, sellerId)` (the
  //       existing Fase 4 method — see Task B2 deviation #7: this
  //       currently has a KNOWN LIMITATION for service orders in
  //       'confirmed' state — `deliverOrder`'s CAS filter accepts
  //       ['shipped','ready_for_pickup'] only — Task B3 follow-up
  //       extends it to 'confirmed' for services OR adds a new
  //       `markServiceDelivered` method).
  //     NO 2FA gate (no financial movement — state transition only).
  //     NO rate limiter.
  //
  // §6 Anti-Invención: response shapes mirror SPEC-081 Fase 5 §5.2
  // verbatim. NO business logic invented.
  //
  // Spec ref: SPEC-081 Fase 5 §5.2 (Seller order routes — 3 new
  //           service-specific endpoints), §4.1 step 2/2'/3 (service
  //           seller flows: confirm OR reject reservation, then mark
  //           delivered); SPEC-NEGOCIO §10.7 (servicios — INV-MKT-15
  //           reject → full refund of deposit).
  // ──────────────────────────────────────────────────────────────────
  /**
   * POST /api/seller/orders/:orderNumber/confirm-reservation — seller
   * confirms availability of a service reservation (CAS pending_seller
   * → confirmed). Sets `serviceReservation.sellerConfirmedAt = now`.
   *
   * NON-financial (no credit movement — just a calendar commitment).
   * After confirmation, the buyer's reservation is guaranteed — the
   * seller is now contractually obligated to render the service OR
   * face auto-close (24h after delivery) or buyer cancellation
   * (subject to `cancellationPolicy`).
   *
   * If the seller does NOT confirm within 72h (INV-MKT-11 — same SLA
   * as physical orders per Q8), the cron `sellerTimeoutJob` calls
   * `rejectServiceReservation` on their behalf with reason
   * "Seller did not confirm within 72h" — full refund of the 50%
   * deposit to the buyer (INV-MKT-15).
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` +
   * `requireMarketOrdersEnabled` + self only. NO 2FA gate.
   *
   * Spec ref: SPEC-081 Fase 5 §5.2 (confirm-reservation endpoint),
   *           §4.1 step 2 (confirmServiceReservation flow);
   *           SPEC-NEGOCIO §10.7 INV-MKT-11 (72h SLA).
   */
  async confirmReservation(
    orderNumber: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<{ status: string }> {
    const url = sellerId
      ? `/seller/orders/${encodeURIComponent(orderNumber)}/confirm-reservation?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/orders/${encodeURIComponent(orderNumber)}/confirm-reservation`;
    return apiFetch<{ status: string }>(url, { method: 'POST', signal });
  },
  /**
   * POST /api/seller/orders/:orderNumber/reject-reservation — seller
   * rejects a service reservation (CAS pending_seller → cancelled +
   * escrowStatus holding → refunded). Credita 100% of the 50% deposit
   * back to the buyer's wallet (INV-MKT-15 — instant full refund when
   * the seller declines availability).
   *
   * Sets `serviceReservation.sellerRejectedAt = now` +
   * `serviceReservation.sellerRejectionReason = reason` (≤500 chars,
   * required — surfaced to the buyer via Socket.IO + email so they
   * understand why the reservation was declined).
   *
   * FINANCIAL-GRADE (INV-MKT-15 refund) — 2FA required (5min
   * `requireRecent2FA` Redis key set by `TwoFactorFinancialDialog`).
   * The caller MUST open the 2FA dialog before calling this method.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` +
   * `requireMarketOrdersEnabled` + `sellerRejectReservationLimiter`
   * (5/min/user) + `requireRecent2FA` (5min 2FA — INV-MKT-15 refund)
   * + self only.
   *
   * Spec ref: SPEC-081 Fase 5 §5.2 (reject-reservation endpoint),
   *           §4.1 step 2' (rejectServiceReservation flow);
   *           SPEC-NEGOCIO §10.7 INV-MKT-15.
   */
  async rejectReservation(
    orderNumber: string,
    reason: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<{ status: string; refundCredits: number }> {
    const url = sellerId
      ? `/seller/orders/${encodeURIComponent(orderNumber)}/reject-reservation?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/orders/${encodeURIComponent(orderNumber)}/reject-reservation`;
    return apiFetch<{ status: string; refundCredits: number }>(url, {
      method: 'POST',
      body: JSON.stringify({ reason }),
      signal,
    });
  },
  /**
   * POST /api/seller/orders/:orderNumber/complete-service — seller
   * marks a service as delivered (CAS confirmed → delivered + sets
   * `deliveredAt = now` + `autoCloseAt = now + 24h`).
   *
   * NON-financial (no credit movement at this stage — the actual
   * escrow release happens at completion, which is triggered by
   * buyer-confirmation OR 24h auto-close). After this transition,
   * the buyer has 24h to confirm receipt; if they don't, the cron
   * `autoCloseJob` fires `completeServiceOrder(orderNumber, {
   * autoClose: true })` per Q10 (releases ONLY the 50% deposit with
   * 30/2/68 split, NO 50% balance charge — the buyer loses the right
   * to charge the balance by not confirming).
   *
   * KNOWN LIMITATION (Task B2 deviation #7): the existing Fase 4
   * `escrowService.deliverOrder` filters its CAS by `status: { $in:
   * ['shipped', 'ready_for_pickup'] }` (does NOT include 'confirmed')
   * — for a service order in 'confirmed' state, this CAS will FAIL
   * with `ORDER_INVALID_TRANSITION` until Task B3 extends the filter.
   *
   * Authz: `requireAuth` + `requireMarketSellersEnabled` +
   * `requireMarketOrdersEnabled` + self only. NO 2FA gate. NO rate
   * limiter.
   *
   * Spec ref: SPEC-081 Fase 5 §5.2 (complete-service endpoint), §4.1
   *           step 3 (markServiceDelivered flow), Q10 (auto-close
   *           behavior); SPEC-NEGOCIO §10.7.
   */
  async completeService(
    orderNumber: string,
    signal?: AbortSignal,
    // Founder directive 2026-10-31 (store switcher bug): pass sellerId
    // so the BE ownership check uses the SELECTED sub-store.
    sellerId?: string,
  ): Promise<{ status: string }> {
    const url = sellerId
      ? `/seller/orders/${encodeURIComponent(orderNumber)}/complete-service?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/orders/${encodeURIComponent(orderNumber)}/complete-service`;
    return apiFetch<{ status: string }>(url, { method: 'POST', signal });
  },
};
